﻿import { AsyncSemaphore } from './AsyncSemaphore.js';

/** This class should only be accessed via the SessionStorageService class. */
class SessionStorageManager {
  constructor(storageKey) {
    this.storageKey = storageKey;
    // See extensive comments in the AsyncSemaphore class (AsyncSemaphore.js) for more on this:
    this._semaphore = new AsyncSemaphore();
  }

  /**
   * Use it to prevent re-entrant access and race conditions. See extensive comments in the
   * AsyncSemaphore class (AsyncSemaphore.js) for more.
   * @returns an "unlock()" callback.
   */
  async lockAccessAsync() {
    return await this._semaphore.lockAsync();
  }

  /* Note, we can't preload or cache the storage object due to concurrency issues related to our
   * extended use of async method. For instance, a page with more than one Designer button will
   * load the session storage more than once, and it's therefore critical that we return the latest
   * version (and not a cached instance). */
  getStorage() {
    const storageAsStr = sessionStorage.getItem(this.storageKey);
    const current = storageAsStr ? JSON.parse(storageAsStr) : {};
    if (this._storage) {
      Object.assign(this._storage, current);
    } else {
      this._storage = current;
    }
    return this._storage;
  }

  save() {
    if (this._storage) {
      sessionStorage.setItem(this.storageKey, JSON.stringify(this._storage));
    } else {
      sessionStorage.removeItem(this.storageKey);
    }
    return this._storage;
  }
}

/** Allows shared, protected access to the sessionStorage built-in object. */
export class SessionStorageService {
  constructor() {
    this.storageManagers = {}
  }

  requestStorageManager(key) {
    let manager = this.storageManagers[key];
    if (!manager) {
      this.storageManagers[key] = manager = new SessionStorageManager(key);
    }
    return manager;
  }
}
